import { getTokenPriceEUR } from "./priceUtils"; // 🔧 Wichtig: für Fallback bei fehlendem Preis
// ✅ Neue Hilfsfunktion einfügen

export function formatDateFromTimestamp(timestamp) {
  if (!timestamp || typeof timestamp !== "number") return "—";
  const date = new Date(timestamp * 1000);
  return date.toISOString().split("T")[0];
}

export function getSafeTimestamp(entry) {
  return entry.timestamp !== undefined ? entry.timestamp : parseDateToTimestamp(entry.date);
}
export function parseDateToTimestamp(dateString) {
  if (!dateString || typeof dateString !== "string") return 0;

  // ISO-Format: YYYY-MM-DD
  if (dateString.includes("-")) {
    const [year, month, day] = dateString.split("-");
    if (!year || !month || !day) return 0;
    return new Date(Number(year), Number(month) - 1, Number(day)).getTime() / 1000;
  }

  // Deutsches Format: TT.MM.JJJJ
  if (dateString.includes(".")) {
    const [day, month, year] = dateString.split(".");
    if (!day || !month || !year) return 0;
    return new Date(Number(year), Number(month) - 1, Number(day)).getTime() / 1000;
  }

  return 0;
}

/**
 * Berechnet Steuerwerte basierend auf bereits zugeordneten FIFO-Zuflüssen (inflows)
 */
export function calculateFifoMatch(nftTx, inflows, nonTaxableInflows = {}, selectedCountry = 'DE') {
  const nftTimestamp = nftTx.timestamp || parseDateToTimestamp(nftTx.date);
  const oneYearSeconds = 365 * 24 * 60 * 60;

  let remaining = nftTx.amount;
  let totalPurchaseEUR = 0;
  let totalTaxFreeEUR = 0;
  let totalTaxableEUR = 0;
  let totalUsedInflowEUR = 0;

  const rows = [];

  // 🎯 HALTEZEIT-STATUS LOG
  // 🔒 HALTEZEIT-STATUS Logging entfernt für Performance
  for (const inflow of inflows) {
    if (remaining <= 0) break;

    const inflowTimestamp =
      inflow.timestamp && inflow.timestamp > 1000000000
        ? inflow.timestamp
        : inflow.block_timestamp || parseDateToTimestamp(inflow.date);

    let heldLongEnough;
    if (selectedCountry === 'IT' || selectedCountry === 'GR') {
      // 🇮🇹 ITALIEN & 🇬🇷 GRIECHENLAND: Keine Haltefrist-Berechnung - alle Transaktionen sind steuerpflichtig
      heldLongEnough = false;
    } else {
      // 🇩🇪 DEUTSCHLAND: Normale 365 Tage Haltefrist
      heldLongEnough = (nftTimestamp - inflowTimestamp >= oneYearSeconds);
    }

    const delta = Math.abs(remaining - inflow.amount);
    const tolerance = 0.1;
    const used = delta <= tolerance ? remaining : Math.min(remaining, inflow.amount);

    const inflowEuroPrice =
      typeof inflow.euroPrice === "number" && inflow.euroPrice > 0
        ? inflow.euroPrice
        : getTokenPriceEUR(inflow.token, inflow.timestamp || inflow.date);

    const nftEuroPrice =
      typeof nftTx.euroPrice === "number"
        ? nftTx.euroPrice
        : getTokenPriceEUR(nftTx.token, nftTimestamp);

    const inflowValueEUR = used * inflowEuroPrice;
    const nftValueEUR = used * nftEuroPrice;
    
    // 🔧 NEU: Prüfe ob dieser Inflow manuell als nicht-steuerpflichtig markiert wurde
    // 🔧 WICHTIG: Verwende die gleiche Key-Generierung wie in FifoDetailRow
    const inflowKey = `${inflow.hash || inflow.timestamp || inflow.date}_${used}_${inflow.amount}`;
    const isManuallyNonTaxable = nonTaxableInflows[inflowKey];
    
    // 🔧 NEU: Gewinn auf 0€ setzen wenn manuell als nicht-steuerpflichtig markiert
    const profit = isManuallyNonTaxable ? 0 : (nftValueEUR - inflowValueEUR);

    totalUsedInflowEUR += inflowValueEUR;
    totalPurchaseEUR += nftValueEUR;

    if (selectedCountry === 'IT' || selectedCountry === 'GR') {
      // 🇮🇹 ITALIEN & 🇬🇷 GRIECHENLAND: Alle Transaktionen sind steuerpflichtig (außer manuell markiert)
      if (isManuallyNonTaxable) {
        totalTaxFreeEUR += nftValueEUR;
      } else {
        // ✅ KORREKT: Gewinne UND Verluste verrechnen (Veräußerungsgeschäfte)
        totalTaxableEUR += profit;
      }
    } else {
      // 🇩🇪 DEUTSCHLAND: Alle Veräußerungsgeschäfte verrechnen (unabhängig von Haltefrist)
      if (isManuallyNonTaxable) {
        totalTaxFreeEUR += nftValueEUR;
      } else {
        // Alle Veräußerungsgeschäfte verrechnen (Gewinne UND Verluste)
        totalTaxableEUR += profit;
      }
    }

    rows.push({
      amount: used,
      einstand: inflowValueEUR,
      verkauf: nftValueEUR,
      gewinn: profit,
      taxable: !isManuallyNonTaxable, // ✅ KORREKT: Alle Veräußerungsgeschäfte verrechnen (unabhängig von Haltefrist)
      heldDays: Math.floor((nftTimestamp - inflowTimestamp) / (60 * 60 * 24)),
      hash: inflow.hash || "",
      isManuallyNonTaxable, // 🔧 NEU: Flag für manuelle Markierung
    });

    remaining -= used;
  }

  return {
    taxableEUR: Math.round(totalTaxableEUR * 100) / 100,
    purchasePriceEUR: Math.round(totalPurchaseEUR * 100) / 100,
    taxFreeBasisEUR: Math.round(totalTaxFreeEUR * 100) / 100,
    totalUsedInflowEUR: Math.round(totalUsedInflowEUR * 100) / 100,
    rows,

  };
}

export function formatDateISO(dateString) {
  if (!dateString || typeof dateString !== "string") return "—";

  if (dateString.includes("-")) return dateString;

  const parts = dateString.split(".");
  if (parts.length !== 3) return "—";

  const [day, month, year] = parts;
  if (!day || !month || !year) return "—";

  return `${year}-${month.padStart(2, "0")}-${day}`;
}

/**
 * ➕ Wendet matchedInflows von einer Liste auf Originaldaten an (nach Hash)
 */
export function mergeMatchedInflows(baseTxs, inflowMatches) {
  return baseTxs.map((tx) => {
    const hash = (tx.hash || "").toLowerCase();
    const match = inflowMatches.find(
      (m) => (m.hash || "").toLowerCase() === hash
    );

    if (match && match.matchedInflows?.length > 0) {
      const sumAmount = match.matchedInflows.reduce((sum, i) => sum + i.amount, 0);
      const sumEinstand = match.matchedInflows.reduce((sum, i) => sum + (i.amount * (i.euroPrice || 0)), 0);

      const heldShort = match.rows?.some(r => r.taxable === true);
      const heldShortText = heldShort ? "⚠️ unter 1 Jahr!" : "✅ steuerfrei";

      console.groupCollapsed(`🔍 FIFO für ${tx.type?.toUpperCase() || "TX"} – ${tx.token} – Hash: ${hash}`);
                              if (match.rows?.length) {
        console.table(
          match.rows.map(r => ({
            Menge: r.amount.toFixed(5),
            EinstandEUR: r.einstand.toFixed(2),
            VerkaufEUR: r.verkauf.toFixed(2),
            Gewinn: r.gewinn.toFixed(2),
            HalteTage: r.heldDays,
            Steuerpflichtig: r.taxable ? "❗ Ja" : "Nein",
            QuelleHash: r.hash.slice(0, 10) + "…",
          }))
        );
      }
      console.groupEnd();
    }

    return {
      ...tx,
      matchedInflows: match?.matchedInflows || [],
      taxableEUR: match?.taxableEUR,
      purchasePriceEUR: match?.purchasePriceEUR,
      taxFreeBasisEUR: match?.taxFreeBasisEUR,
    };
  });
}